import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { SelectModule } from 'primeng/select';
import { filter, take } from 'rxjs';
import { RootState } from '../../../../../store/reducers/RootState.model';
import { UoMSettingActions } from '../../../../../store/workflow/actions/user-profile.actions';
import {
  selectUoMSetting,
  selectUserProfileError,
} from '../../../../../store/workflow/selectors/user-profile.selectors';
import { LoadingDirective } from '../../../../directives/loading/loading.directive';
import {
  IUoMSetting,
  IUserSetting,
} from '../../../../models/workflow/user-setting';

@Component({
  selector: 'app-uom-setting',
  imports: [
    SelectModule,
    FormsModule,
    ButtonModule,
    ReactiveFormsModule,
    FormsModule,
    LoadingDirective,
  ],
  templateUrl: './uom-setting.component.html',
  styleUrl: './uom-setting.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UomSettingComponent implements OnInit {
  #store = inject(Store<RootState>);
  toastr = inject(MessageService);

  form: FormGroup | undefined;
  userSetting = signal<IUserSetting>({ username: '', uoMSettings: [] });

  constructor(private dialogRef: DynamicDialogRef) {}

  ngOnInit(): void {
    this.#store.dispatch(UoMSettingActions.loadUoMSetting());
    this.#store
      .select(selectUoMSetting)
      .pipe(
        filter((setting) => !!setting),
        take(1),
      )
      .subscribe((resp) => {
        if (resp) {
          this.userSetting.set({
            ...resp,
            uoMSettings: resp.uoMSettings.map((setting) => ({
              ...setting,
              key: setting.selectedUoMId,
              options: setting.uoMs?.map((uom) => ({
                label: uom.displayName,
                value: uom.id,
              })),
            })),
          });
        }
        this.form = this.toFormGroup(this.userSetting().uoMSettings);
      });
  }

  resetToDefault() {
    const defaultValues = this.userSetting().uoMSettings.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.key!]: curr.uoMs?.find((x) => x.isDefault)?.id,
      }),
      {},
    );

    this.form?.setValue(defaultValues);
    this.form?.markAsDirty();
  }

  apply() {
    const payload: IUserSetting = {
      username: this.userSetting().username,
      uoMSettings: this.userSetting().uoMSettings.map((setting) => ({
        uoMcategoryId: setting.uoMcategoryId,
        uoMCategoryName: setting.uoMCategoryName,
        uoMs: setting.uoMs,
        selectedUoMId: this.form?.get(setting.key!)?.value || undefined,
      })),
    };

    this.#store.dispatch(
      UoMSettingActions.applyUoMSetting({ uomSetting: payload }),
    );

    this.#store
      .select(selectUserProfileError)
      .pipe(
        filter((error) => error !== undefined),
        take(1),
      )
      .subscribe((error) => {
        if (error) {
          this.toastr.add({
            severity: 'error',
            summary: 'Error',
            detail: `Error in updating the UoM Settings. Please try again later.`,
            closable: true,
          });
        } else {
          this.toastr.add({
            severity: 'success',
            summary: 'Update Success',
            detail: 'The UoM Settings has been updated successfully.',
            closable: true,
          });
          this.close();
        }
      });
  }

  close() {
    this.dialogRef.close();
  }

  private toFormGroup(uomCates: IUoMSetting[]) {
    const group = uomCates.reduce((acc, curr) => {
      return {
        ...acc,
        [curr.key || '']: new FormControl(
          curr.selectedUoMId,
          Validators.required,
        ),
      };
    }, {});
    return new FormGroup(group);
  }
}
